import { Chip, Tooltip, Box, Typography } from '@mui/material';
import { FC, useMemo } from 'react';
import { CustomerDto } from '../../dtos';
import { AccountStatus } from '../../dtos/enums/AccountStatus';
import { Warning, Error } from '@mui/icons-material';
import { formatCurrency, formatDate } from '../../util';

export interface ICustomerStatusChipProps {
    customer: CustomerDto;
}

export const CustomerStatusChip: FC<ICustomerStatusChipProps> = ({ customer }) => {
    const chipColor = useMemo(() => {
        switch (customer.accountStatus) {
            case AccountStatus.CreditMessage:
                return 'warning';
            case AccountStatus.OnHold:
                return 'error';
            default:
                return 'default';
        }
    }, [customer.accountStatus]);

    const icon = useMemo(() => {
        switch (customer.accountStatus) {
            case AccountStatus.CreditMessage:
                return <Warning fontSize="small" />;
            case AccountStatus.OnHold:
                return <Error fontSize="small" />;
            default:
                return undefined;
        }
    }, [customer.accountStatus]);

    const formattedDate = useMemo(() => {
        if (!customer.accountHighCreditDate) return 'N/A';
        return formatDate(customer.accountHighCreditDate);
    }, [customer.accountHighCreditDate]);

    const tooltipContent = useMemo(() => {
        if (customer.accountStatus === AccountStatus.Normal || !icon) {
            return null;
        }

        return (
            <Box sx={{ p: 0.5, minWidth: '300px', maxWidth: '600px' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    Customer {customer.accountStatus}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Credit Limit: {formatCurrency(customer.accountCreditLimit)} | High Credit Date: {formattedDate} | High Credit Amount: {formatCurrency(customer.accountHighCreditAmount)}
                </Typography>
                {customer.accountComment && (
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 5,
                            WebkitBoxOrient: 'vertical',
                            wordBreak: 'break-word'
                        }}
                    >
                        {customer.accountComment}
                    </Typography>
                )}
            </Box>
        );
    }, [customer, formattedDate, icon]);

    if (customer.accountStatus === AccountStatus.Normal || !icon) {
        return null;
    }

    return (
        <Tooltip 
            title={tooltipContent} 
            arrow
            componentsProps={{
                tooltip: {
                    sx: {
                        maxWidth: 'none',
                    }
                }
            }}
        >
            <Chip 
                label={customer.accountStatus} 
                color={chipColor}
                size="small"
                icon={icon}
                sx={{
                    padding: '5px',
                }}
            />
        </Tooltip>
    );
};

